// Get Bootstrap variant for status badges
export const getStatusVariant = (status) => {
  const statusMap = {
    active: 'success',
    pending: 'warning',
    inactive: 'secondary',
    error: 'danger',
    completed: 'success',
    cancelled: 'danger',
    draft: 'info',
  };

  return statusMap[status?.toLowerCase()] || 'secondary';
};

export const getBadgeClass = (status) => {
  const variant = getStatusVariant(status);
  return `badge bg-${variant}`;
};

export const formatCurrency = (amount, currency = 'EUR') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const formatStatusText = (text) => {
  if (!text) return '';

  const lowerText = text.toLowerCase();

  // Handle 'pending' prefix - capitalize last 3 letters
  if (lowerText.startsWith('pending')) {
    const prefix = 'Pending';
    const suffix = text.slice(7).toUpperCase();
    return prefix + suffix;
  }

  // Capitalize first letter for all other cases
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Centralized status color mapping for badges
export const setStatusColor = (status) => {
  const normalizedStatus = status?.toLowerCase().trim();

  switch (normalizedStatus) {
    case 'active':
    case 'confirmed':
    case 'existing':
    case 'new recruit':
      return 'success';
    case 'new':
      return 'primary';
    case 'cancelled':
    case 'inactive':
    case 'leave of absence':
    case 'resigned':
      return 'danger';
    case 'arriving':
    case 'departing':
    case 'end of season':
    case 'start of season':
      return 'secondary';
    case 'modified':
    case 'pending':
    case 'pendingbtt':
    case 'pendingdes':
    case 'pendinghr':
      return 'warning';
    case 'ready to communicate':
    case 'want 2nd offer':
    case 'want 2nd offer accepted':
    case 'want 2nd offer to communicate':
      return 'info';
    default:
      return 'secondary';
  }
};

// Outlook-inspired React Select styles for dark mode
export const getReactSelectStyles = (darkMode, hasError = false) => ({
  control: (base, state) => {
    let bgColor = base.backgroundColor;
    if (darkMode) {
      bgColor = state.isDisabled ? '#1f1f1f' : '#2d2d2d';
    }

    let border = base.borderColor;
    if (hasError) {
      border = '#d13438';
    } else if (darkMode) {
      border = state.isFocused ? '#0078d4' : '#484848';
    }

    return {
      ...base,
      backgroundColor: bgColor,
      borderColor: border,
      color: darkMode ? '#ffffff' : base.color,
      opacity: state.isDisabled ? 0.6 : 1,
      boxShadow: state.isFocused && darkMode ? '0 0 0 1px #0078d4' : base.boxShadow,
      '&:hover': {
        borderColor: darkMode ? '#0078d4' : base['&:hover']?.borderColor,
      },
    };
  },
  menu: (base) => ({
    ...base,
    backgroundColor: darkMode ? '#2d2d2d' : base.backgroundColor,
    border: darkMode ? '1px solid #484848' : base.border,
    boxShadow: darkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : base.boxShadow,
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
  }),
  option: (base, state) => {
    let bgColor = base.backgroundColor;
    if (state.isSelected) {
      bgColor = '#0078d4';
    } else if (state.isFocused) {
      bgColor = darkMode ? '#323232' : '#deebff';
    } else if (darkMode) {
      bgColor = '#2d2d2d';
    }

    return {
      ...base,
      backgroundColor: bgColor,
      color: state.isSelected || darkMode ? '#ffffff' : base.color,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: darkMode ? '#005a9e' : '#b3d4ff',
      },
    };
  },
  singleValue: (base) => ({
    ...base,
    color: darkMode ? '#ffffff' : base.color,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: darkMode ? '#323232' : base.backgroundColor,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: darkMode ? '#ffffff' : base.color,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: darkMode ? '#b3b3b3' : base.color,
    '&:hover': {
      backgroundColor: darkMode ? '#0078d4' : base['&:hover']?.backgroundColor,
      color: darkMode ? '#ffffff' : base['&:hover']?.color,
    },
  }),
  input: (base) => ({
    ...base,
    color: darkMode ? '#ffffff' : base.color,
  }),
  placeholder: (base) => ({
    ...base,
    color: darkMode ? '#8a8a8a' : base.color,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: darkMode ? '#b3b3b3' : base.color,
    '&:hover': {
      color: darkMode ? '#0078d4' : base['&:hover']?.color,
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: darkMode ? '#b3b3b3' : base.color,
    '&:hover': {
      color: darkMode ? '#0078d4' : base['&:hover']?.color,
    },
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: darkMode ? '#484848' : base.backgroundColor,
  }),
});
